import { useCallback, useEffect, useRef, useState } from "react";
import { FunnelConfigTypes } from "../../funnel/types";
import { FUNNEL_MEASUREMENT_DELAY_MS } from "../constants";
import { useContentHeightMeasurement } from "../../../hooks/useContentHeightMeasurement";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";

interface UseSlideInMenuContainerProps {
  openMenu?: SLIDE_IN_MENUS;
  onCloseMenu: () => void;
  enabled?: boolean;
}

export function useSlideInMenuContainer({
  openMenu,
  onCloseMenu,
  enabled = true,
}: UseSlideInMenuContainerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track funnel state for sizing
  const [funnelConfig, setFunnelConfig] = useState<
    FunnelConfigTypes | undefined
  >(undefined);
  const hasFunnel = !!funnelConfig;

  // Delay width transition to sync with height measurement
  const [delayedHasFunnel, setDelayedHasFunnel] = useState(hasFunnel);

  const onFunnelChange = useCallback(
    (config: FunnelConfigTypes | undefined) => {
      setFunnelConfig(config);
    },
    [],
  );

  // Escape key handler
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && openMenu) {
        event.preventDefault();
        event.stopPropagation();
        onCloseMenu();
      }
    };

    if (openMenu) {
      document.addEventListener("keydown", handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [openMenu, onCloseMenu]);

  // Clear funnel when the open menu changes (navigating between menus)
  useEffect(() => {
    setFunnelConfig(undefined);
  }, [openMenu]);

  // Reset scroll position when funnel state changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [funnelConfig]);

  // Sync delayed width with height measurement timing
  // Only delay when opening funnel (entering), not when closing (exiting)
  useEffect(() => {
    if (!enabled) return;

    // When closing funnel, update immediately (no measurement needed)
    if (!hasFunnel) {
      setDelayedHasFunnel(false);
      return;
    }

    // When opening funnel, delay to sync with height measurement
    const timer = setTimeout(() => {
      setDelayedHasFunnel(true);
    }, FUNNEL_MEASUREMENT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [hasFunnel, enabled]);

  // Measure content height when funnel changes
  const contentHeight = useContentHeightMeasurement({
    contentRef,
    dependencies: [funnelConfig],
    enabled: enabled && hasFunnel,
  });

  return {
    contentRef,
    scrollContainerRef,
    hasFunnel,
    delayedHasFunnel,
    contentHeight,
    onFunnelChange,
  };
}
